import { stage } from './stage';

export interface AuthConfig {
  readonly userPoolId: string;
  readonly userPoolClientId: string;
  /**
   * Cognito Identity Pool. Used to mint guest IAM credentials for
   * signed (`AuthType: AWS_IAM`) calls to the public availability
   * endpoints. Both stages share the same pool-per-stage; signed-in
   * users get the authenticated role, signed-out users get the
   * unauthenticated role — both are scoped to invoke just the
   * availability Lambdas (see `infra/.../identity-stack.ts`).
   */
  readonly identityPoolId: string;
  readonly region: string;
  /** SigV4-authed Lambda Function URL for username-availability. */
  readonly usernameCheckUrl: string;
  /** SigV4-authed Lambda Function URL for email-availability. */
  readonly emailCheckUrl: string;
  /**
   * Public (no auth) Function URL — Device B POSTs the approval token
   * here to approve a pending sign-in started on Device A.
   */
  readonly authApproveUrl: string;
  /**
   * Public (no auth) Function URL — Device A polls here every ~2s on
   * `/auth/check-email` until the approval flips from `pending` to
   * `approved`.
   */
  readonly authStatusUrl: string;
  /**
   * Public reCAPTCHA v3 site key for the sign-in gate. Safe to ship to
   * the browser; the matching secret lives only in the sibling infra
   * project, where `DefineAuthChallenge` verifies the token and fails
   * closed (see docs/auth.md §13). One key across stages — it's
   * registered for the oculusmind.ai domains (+ `localhost` for dev).
   */
  readonly recaptchaSiteKey: string;
  /**
   * Public (no-auth) client-telemetry ingest — the browser POSTs (via
   * `navigator.sendBeacon`) a CLIENT-SIDE reCAPTCHA mint failure here so it
   * lands in the same CloudWatch failure log as server-side rejections.
   * These failures never reach Cognito (no token was minted), so without
   * this beacon they are invisible. Owned by the sibling infra repo's
   * OmIdentity{Stage} stack, fronted at `/auth/client-event` on the stage
   * API Gateway. Best-effort: an empty value disables reporting.
   */
  readonly clientEventUrl: string;
}

// Public reCAPTCHA v3 site key (oculusmind.ai + localhost). Public by
// design — the secret half is infra-only. Shared across all stages.
const RECAPTCHA_SITE_KEY = '6Lc7zg0tAAAAAJVztMFbNXu9Uf9Ody9RspnE1slr';

// Pool / client IDs are public (no secrets) and safe to ship to the browser.
// They are owned by the sibling infra repo's OmIdentity{Stage} stacks; the
// values below mirror the deployed state as of 2026-05-20.
//
// Phase 2+ will replace these defaults with a build-time SSM lookup so the
// repo doesn't carry hard-coded values:
//   /oculusmind/{stage}/identity/user-pool-id
//   /oculusmind/{stage}/identity/web-client-id
//
// Until then, callers can override at build time:
//   VITE_USER_POOL_ID=... VITE_USER_POOL_CLIENT_ID=... npm run build:beta
// Availability-check + auth endpoints — produced by OmIdentity{Stage} and
// fronted at `/auth/*` on the stage API Gateway (api[-beta].oculusmind.ai).
// Both stages bake in their own prod-/beta-specific defaults below; these
// values are public (pool IDs + public API Gateway URLs, no secrets) and
// safe to ship to the browser. The `VITE_*` env vars still override at
// build time for CI, but no env setup is required for a normal build.
//
// `prod-never-calls-beta` rule: prod uses the `PROD_*` constants, which are
// prod-specific — prod can't fall back to beta because beta's endpoints are
// never referenced from the prod config block.
//
// (Pre-2026-05-30 these were direct Lambda Function URLs signed against the
// `lambda` service; they now sit behind API Gateway — the username/email
// checks are IAM-authed via `execute-api`, so signedFetch signs that.)
const BETA_USERNAME_CHECK_URL =
  'https://api-beta.oculusmind.ai/auth/username-check';
const BETA_EMAIL_CHECK_URL = 'https://api-beta.oculusmind.ai/auth/email-check';
const BETA_AUTH_APPROVE_URL = 'https://api-beta.oculusmind.ai/auth/approve';
const BETA_AUTH_STATUS_URL = 'https://api-beta.oculusmind.ai/auth/status';
const BETA_CLIENT_EVENT_URL = 'https://api-beta.oculusmind.ai/auth/client-event';
const BETA_IDENTITY_POOL_ID = 'us-west-2:992eeb26-3eec-4bd2-9d1b-b5b36de3d6a1';

// Prod defaults — from the OmIdentityProd stack outputs (us-west-2).
const PROD_USERNAME_CHECK_URL = 'https://api.oculusmind.ai/auth/username-check';
const PROD_EMAIL_CHECK_URL = 'https://api.oculusmind.ai/auth/email-check';
const PROD_AUTH_APPROVE_URL = 'https://api.oculusmind.ai/auth/approve';
const PROD_AUTH_STATUS_URL = 'https://api.oculusmind.ai/auth/status';
const PROD_CLIENT_EVENT_URL = 'https://api.oculusmind.ai/auth/client-event';
const PROD_IDENTITY_POOL_ID = 'us-west-2:b2531aff-6833-4c36-8a33-fbce51bcfa26';

function buildConfig(s: 'local' | 'beta' | 'prod'): AuthConfig {
  if (s === 'prod') {
    return {
      userPoolId:
        import.meta.env.VITE_USER_POOL_ID || 'us-west-2_zBlLF5Gas',
      userPoolClientId:
        import.meta.env.VITE_USER_POOL_CLIENT_ID ||
        '2u1hj3ae0lqc9gtk494e57rece',
      identityPoolId:
        import.meta.env.VITE_IDENTITY_POOL_ID || PROD_IDENTITY_POOL_ID,
      region: 'us-west-2',
      usernameCheckUrl:
        import.meta.env.VITE_USERNAME_CHECK_URL || PROD_USERNAME_CHECK_URL,
      emailCheckUrl:
        import.meta.env.VITE_EMAIL_CHECK_URL || PROD_EMAIL_CHECK_URL,
      authApproveUrl:
        import.meta.env.VITE_AUTH_APPROVE_URL || PROD_AUTH_APPROVE_URL,
      authStatusUrl:
        import.meta.env.VITE_AUTH_STATUS_URL || PROD_AUTH_STATUS_URL,
      recaptchaSiteKey:
        import.meta.env.VITE_RECAPTCHA_SITE_KEY || RECAPTCHA_SITE_KEY,
      clientEventUrl:
        import.meta.env.VITE_CLIENT_EVENT_URL || PROD_CLIENT_EVENT_URL,
    };
  }
  // local + beta both target the beta backend.
  return {
    userPoolId: import.meta.env.VITE_USER_POOL_ID || 'us-west-2_ASNp0BsKF',
    userPoolClientId:
      import.meta.env.VITE_USER_POOL_CLIENT_ID || '582j9rlricn253aqvmnrl2rvpl',
    identityPoolId:
      import.meta.env.VITE_IDENTITY_POOL_ID || BETA_IDENTITY_POOL_ID,
    region: 'us-west-2',
    usernameCheckUrl:
      import.meta.env.VITE_USERNAME_CHECK_URL || BETA_USERNAME_CHECK_URL,
    emailCheckUrl:
      import.meta.env.VITE_EMAIL_CHECK_URL || BETA_EMAIL_CHECK_URL,
    authApproveUrl:
      import.meta.env.VITE_AUTH_APPROVE_URL || BETA_AUTH_APPROVE_URL,
    authStatusUrl:
      import.meta.env.VITE_AUTH_STATUS_URL || BETA_AUTH_STATUS_URL,
    recaptchaSiteKey:
      import.meta.env.VITE_RECAPTCHA_SITE_KEY || RECAPTCHA_SITE_KEY,
    clientEventUrl:
      import.meta.env.VITE_CLIENT_EVENT_URL || BETA_CLIENT_EVENT_URL,
  };
}

export const authConfig: AuthConfig = buildConfig(stage);
